import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

const logoImage = '/notifyer-logo.svg'

const navLinks = [
  { name: 'Features', href: '#features' },
  { name: 'Vibe Code', href: '/vibe-code' },
  { name: 'Integrations', href: '#integrations' },
  { name: 'How It Works', href: '#how-it-works' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'FAQs', href: '#faqs' },
]

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const isHomePage = typeof window !== 'undefined' ? window.location.pathname === '/' : true
  const activePath = location.pathname

  const openEmailModal = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openEmailModal', { detail: { source: 'navigation_cta' } }))
    }
  }

  const formatHref = (href) => {
    if (href.startsWith('#')) {
      return isHomePage ? href : `/${href}`
    }
    return href
  }

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  return (
    <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-white/20 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 md:px-8 md:py-4 lg:px-12 xl:px-[100px]">
        {/* Mobile: Menu button and Logo */}
        <div className="flex items-center gap-3 lg:hidden">
          {/* Mobile menu button */}
          <button
            className="flex flex-col gap-1 p-1.5 touch-manipulation"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`h-0.5 w-5 bg-[#1c2329] transition-all duration-300 ${isMobileMenuOpen ? 'translate-y-1.5 rotate-45' : ''}`} />
            <span className={`h-0.5 w-5 bg-[#1c2329] transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`h-0.5 w-5 bg-[#1c2329] transition-all duration-300 ${isMobileMenuOpen ? '-translate-y-1.5 -rotate-45' : ''}`} />
          </button>
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img
              src={logoImage}
              alt="Notifyer Logo"
              className="h-5 sm:h-6 object-contain"
              style={{ width: 'auto', aspectRatio: '816.63645 / 132.29167' }}
            />
          </Link>
        </div>

        {/* Desktop: Logo */}
        <Link to="/" className="hidden lg:flex flex-shrink-0 items-center">
          <img
            src={logoImage}
            alt="Notifyer Logo"
            className="h-5 sm:h-6 object-contain"
            style={{ width: 'auto', aspectRatio: '816.63645 / 132.29167' }}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-6 xl:gap-8 lg:flex">
          {navLinks.map((link) => (
            link.href.startsWith('/') ? (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm xl:text-[16px] font-medium tracking-[-0.4px] transition-colors ${
                  activePath === link.href ? 'text-[#00a85d]' : 'text-[#1c2329] hover:text-[#111]'
                }`}
              >
                {link.name}
              </Link>
            ) : (
              <a
                key={link.name}
                href={formatHref(link.href)}
                className="text-sm xl:text-[16px] font-medium tracking-[-0.4px] text-[#1c2329] hover:text-[#111] transition-colors"
              >
                {link.name}
              </a>
            )
          ))}
        </div>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 xl:gap-3 lg:flex">
          <a href="https://console.notifyer-systems.com/sign-in" className="h-10 xl:h-11 w-[100px] xl:w-[110px] rounded-full border border-[#1c2329] text-sm xl:text-[15px] font-semibold text-[#1c2329] shadow-[0px_3px_8px_rgba(36,196,211,0.12)] hover:bg-gray-50 transition-colors flex items-center justify-center">
            Log In
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEmailModal()
            }}
            className="h-10 xl:h-11 px-5 xl:px-6 rounded-full bg-[#1c2329] text-sm xl:text-[15px] font-semibold text-white shadow-[0px_3px_8px_rgba(36,196,211,0.12)] hover:bg-[#0f1720] transition-colors flex items-center justify-center whitespace-nowrap"
          >
            Get Started
          </a>
        </div>

        {/* Mobile: Get Started button */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            openEmailModal()
          }}
          className="lg:hidden h-8 px-3.5 rounded-full border border-[#1c2329] text-xs font-semibold text-[#1c2329] hover:bg-gray-50 active:bg-gray-100 transition-all touch-manipulation flex items-center justify-center whitespace-nowrap shadow-sm"
        >
          Get Started
        </a>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden top-[57px] sm:top-[65px]"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu */}
      <div className={`lg:hidden fixed top-[57px] sm:top-[65px] left-0 right-0 bg-white/90 backdrop-blur-md border-t border-white/20 shadow-lg transition-all duration-300 ease-in-out overflow-y-auto ${isMobileMenuOpen ? 'max-h-[calc(100vh-57px)] sm:max-h-[calc(100vh-65px)] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        }`}>
        <div className="px-4 py-6 sm:px-6">
          <div className="flex flex-col gap-5 sm:gap-6">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-base sm:text-lg font-medium tracking-[-0.4px] py-2 transition-colors touch-manipulation ${
                    activePath === link.href ? 'text-[#00a85d]' : 'text-[#1c2329] hover:text-[#111]'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={formatHref(link.href)}
                  className="text-base sm:text-lg font-medium tracking-[-0.4px] text-[#1c2329] py-2 hover:text-[#111] transition-colors touch-manipulation"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              )
            ))}
          </div>
          <div className="mt-6 sm:mt-8 flex flex-col gap-3 sm:gap-4">
            <a href="https://console.notifyer-systems.com/sign-in" className="h-12 sm:h-14 w-full rounded-full border-2 border-[#1c2329] text-base sm:text-lg font-semibold text-[#1c2329] shadow-[0px_3px_8px_rgba(36,196,211,0.12)] hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation flex items-center justify-center" onClick={() => setIsMobileMenuOpen(false)}>
              Log In
            </a>
            <a
              href="#"
              className="h-12 sm:h-14 w-full rounded-full bg-[#1c2329] text-base sm:text-lg font-semibold text-white shadow-[0px_3px_8px_rgba(36,196,211,0.12)] hover:bg-[#0f1720] active:bg-[#0a0f15] transition-colors touch-manipulation flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault()
                setIsMobileMenuOpen(false)
                openEmailModal()
              }}
            >
              Get Started
            </a>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navigation

